import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams, useNavigate } from "react-router-dom";
import { useUserAuth } from "../../../context/UserAuthContext";
import axios from "axios";
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';

// Material UI Components
import {
    Container,
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    Button,
    IconButton,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Paper,
    Avatar,
    LinearProgress,
    Tooltip,
    Fab,
    Chip,
    Badge,
    FormControlLabel,
    Radio,
    RadioGroup
} from "@mui/material";

import {
    LocationCity as VenueIcon,
    Nature as LawnIcon,
    MeetingRoom as BanquetIcon,
    Pool as PoolIcon,
    Hotel as RoomIcon,
    Palette as DecorIcon,

    ArrowBack as BackIcon,
    AddPhotoAlternate as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    Check as CheckIcon,
    Upload as UploadIcon,
} from "@mui/icons-material";
const TagImageManagement = () => {
    const { tag_name } = useParams();
    const navigate = useNavigate();
    const { user, language, token } = useUserAuth();
    const merchant_id = user.uid;
    const base_url = import.meta.env.VITE_LOCAL_BASE_URL;

    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [images, setImages] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);
    const [editImageFile, setEditImageFile] = useState(null);

    const [tagDialogOpen, setTagDialogOpen] = useState(false);
    const [selectedTag, setSelectedTag] = useState("");

    // Map tag IDs to display names
    const tagNames = {
        venue_overview: language === "en" ? "Venue Overview" : "वेन्यू अवलोकन",
        lawn: language === "en" ? "Lawn" : "लॉन",
        banquet_hall: language === "en" ? "Banquet Hall" : "बैंकेट हॉल",
        swimming_pool: language === "en" ? "Swimming Pool" : "स्विमिंग पूल",
        rooms: language === "en" ? "Rooms" : "कमरे",
        decorations: language === "en" ? "Decorations" : "सजावट",
    };

    const currentTagName = tagNames[tag_name] || tag_name;

    // Fetch images for specific tag
    const fetchImages = async () => {
        try {
            setLoading(true);
            const res = await axios.get(
                `${base_url}/api/v1/previous-work/${merchant_id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            // Filter images by tag_name
            if (res.data.data && Array.isArray(res.data.data)) {
                const filteredImages = res.data.data.filter(
                    (image) => image.tag_name === tag_name ||
                        (!image.tag_name && tag_name === "venue_overview") // Default tag
                );
                setImages(filteredImages);
            } else {
                setImages([]);
            }
        } catch (error) {
            console.error("Error fetching images:", error);
            toast.error(
                language === "en"
                    ? "Failed to load images"
                    : "छवियां लोड करने में विफल"
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!merchant_id) return;
        fetchImages();
    }, [merchant_id, tag_name]);

const validateImageDimensions = (file) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      const width = img.width;
      const height = img.height;

      URL.revokeObjectURL(img.src);

      const isLandscapeValid = width >= 1024 && height >= 683;
      const isPortraitValid = width >= 683 && height >= 1024;

      if (isLandscapeValid || isPortraitValid) {
        resolve({ valid: true });
      } else {
        resolve({
          valid: false,
          message: `Image ${file.name} must be at least 1024x683 (landscape) or 683x1024 (portrait). Current: ${width}x${height}`,
        });
      }
    };

    img.onerror = () => {
      resolve({
        valid: false,
        message: `${file.name} is not a valid image`,
      });
    };
  });
};

const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    try {
        setUploading(true);
        setUploadProgress(0);

        let successCount = 0;
        let failMessages = [];

        for (const file of files) {
            const validation = await validateImageDimensions(file);

            if (!validation.valid) {
                failMessages.push(validation.message);
                continue;
            }

            try {
                const formData = new FormData();
                formData.append("merchant_id", merchant_id);
                formData.append("image", file);
                formData.append("tag_name", tag_name);

                await axios.post(
                    `${base_url}/api/v1/previous-work/image-upload/${merchant_id}`,
                    formData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "multipart/form-data",
                        },
                    }
                );

                successCount++;
            } catch (err) {
                failMessages.push(`Failed to upload ${file.name}`);
            }
        }

        if (successCount > 0) {
            toast.success(`${successCount} image(s) uploaded successfully`);
            fetchImages();
        }

        if (failMessages.length > 0) {
            toast.error(failMessages.join("\n"));
        }

    } catch (error) {
        console.error(error);
        toast.error("Upload failed");
    } finally {
        setUploading(false);
        setUploadProgress(0);
        event.target.value = "";
    }
};

    // Handle image deletion
    const handleDeleteImage = async () => {
        if (!imageToDelete) return;

        try {
            await axios.delete(
                `${base_url}/api/v1/previous-work/${imageToDelete.id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            toast.success(
                language === "en"
                    ? "Image deleted successfully"
                    : "छवि सफलतापूर्वक हटाई गई"
            );
            setImages(images.filter((img) => img.id !== imageToDelete.id));
        } catch (error) {
            console.error("Error deleting image:", error);
            toast.error(
                language === "en"
                    ? "Failed to delete image"
                    : "छवि हटाने में विफल"
            );
        } finally {
            setDeleteDialogOpen(false);
            setImageToDelete(null);
        }
    };

   const handleUpdateImage = async () => {
    if (!selectedImage || !editImageFile) return;

    const result = await validateImageDimensions(editImageFile);

    if (!result.valid) {
        toast.error(result.error);
        return;
    }

    try {
        const formData = new FormData();
        formData.append("image", editImageFile);

        await axios.put(
            `${base_url}/api/v1/previous-work/${selectedImage.id}`,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        toast.success(
            language === "en"
                ? "Image updated successfully"
                : "छवि सफलतापूर्वक अपडेट हुई"
        );

        fetchImages();
        setEditDialogOpen(false);
        setSelectedImage(null);
        setEditImageFile(null);

    } catch (error) {
        toast.error(
            language === "en"
                ? "Failed to update image"
                : "छवि अपडेट करने में विफल"
        );
    }
};

    // Set image as primary
    const handleSetPrimary = async (imageId) => {
        try {
            await axios.put(
                `${base_url}/api/v1/vendors/previous-work/${imageId}/set-primary`,
                { user_id: merchant_id },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            toast.success(
                language === "en"
                    ? "Set as primary image"
                    : "प्राथमिक छवि के रूप में सेट किया गया"
            );
            fetchImages(); // Refresh to update primary status
        } catch (error) {
            console.error("Error setting primary image:", error);
            toast.error(
                language === "en"
                    ? "Failed to set primary image"
                    : "प्राथमिक छवि सेट करने में विफल"
            );
        }
    };
    const handleUpdateTag = async () => {
        try {
            const res = await axios.put(
                `${base_url}/api/v1/previous-work/update-tag`,
                {
                    image_id: selectedImage.id,
                    tag_name: selectedTag,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setImages(images.filter((img) => img.id !== selectedImage.id));
            toast.success(language === "en" ? "Tag updated" : "टैग अपडेट हो गया");  
            setTagDialogOpen(false);
        } catch (error) {
            toast.error("Failed to update tag");
        }
    };
    const tags = [
        {
            id: "venue_overview",
            name: language === "en" ? "Venue Overview" : "वेन्यू अवलोकन",
            icon: <VenueIcon sx={{ fontSize: 32 }} />,
            bgColor: "#f5f7fa",
        },
        {
            id: "lawn",
            name: language === "en" ? "Lawn" : "लॉन",
            icon: <LawnIcon sx={{ fontSize: 32 }} />,
            bgColor: "#f0f9f0",
        },
        {
            id: "banquet_hall",
            name: language === "en" ? "Banquet Hall" : "बैंकेट हॉल",
            icon: <BanquetIcon sx={{ fontSize: 32 }} />,
            bgColor: "#fff8f0",
        },
        {
            id: "swimming_pool",
            name: language === "en" ? "Swimming Pool" : "स्विमिंग पूल",
            icon: <PoolIcon sx={{ fontSize: 32 }} />,
            bgColor: "#f0f9fc",
        },
        {
            id: "rooms",
            name: language === "en" ? "Rooms" : "कमरे",
            icon: <RoomIcon sx={{ fontSize: 32 }} />,
            bgColor: "#f9f0fc",
        },
        {
            id: "decorations",
            name: language === "en" ? "Decorations" : "सजावट",
            icon: <DecorIcon sx={{ fontSize: 32 }} />,
            bgColor: "#fcf0f5",
        },
    ];
    return (
        <div className="page-wrapper">
            <div className="container-fluid">
                <div className="card py-4">
                    <Container maxWidth="lg">
                        {/* Header */}
                        <Paper
                            elevation={0}
                            sx={{
                                mb: 3,
                                p: 3,
                                backgroundColor: "#085f9a",
                                color: "white",
                                borderRadius: 2,
                                position: "relative",
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <IconButton
                                    onClick={() => navigate("/dashboard/venue-previous-work")}
                                    sx={{ color: "white" }}
                                >
                                    <BackIcon />
                                </IconButton>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                        {currentTagName}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                                        {language === "en"
                                            ? `Manage your ${currentTagName.toLowerCase()} images`
                                            : `अपनी ${currentTagName.toLowerCase()} छवियां प्रबंधित करें`}
                                    </Typography>
                                </Box>
                                <Badge
                                    badgeContent={images.length}
                                    color="secondary"
                                    sx={{ mr: 2 }}
                                >
                                    <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)" }}>
                                        <UploadIcon />
                                    </Avatar>
                                </Badge>
                            </Box>
                        </Paper>

                        {/* Upload Section */}
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <input
                                type="file"
                                id="image-upload"
                                hidden
                                multiple
                                accept="image/*"
                                onChange={handleImageUpload}
                                disabled={uploading}
                            />
                            <label htmlFor="image-upload">
                                <Button
                                    component="span"
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    disabled={uploading}
                                    fullWidth
                                    sx={{ py: 1.5 }}
                                >
                                    {language === "en"
                                        ? `Upload ${currentTagName} Images`
                                        : `${currentTagName} छवियां अपलोड करें`}
                                </Button>
                            </label>

                            {uploading && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                        {language === "en" ? "Uploading..." : "अपलोड हो रहा है..."}
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={uploadProgress}
                                        sx={{ height: 8, borderRadius: 4 }}
                                    />
                                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                                        {uploadProgress}%
                                    </Typography>
                                </Box>
                            )}
                        </Paper>

                        {/* Images Grid */}
                        {loading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                                <CircularProgress />
                            </Box>
                        ) : images.length === 0 ? (
                            <Paper sx={{ p: 5, textAlign: "center" }}>
                                <Typography variant="h6" color="textSecondary" gutterBottom>
                                    {language === "en"
                                        ? "No images uploaded yet"
                                        : "अभी तक कोई छवि अपलोड नहीं की गई है"}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                                    {language === "en"
                                        ? `Upload images for ${currentTagName} to showcase your venue`
                                        : `अपने वेन्यू को प्रदर्शित करने के लिए ${currentTagName} की छवियां अपलोड करें`}
                                </Typography>
                                <label htmlFor="image-upload">
                                    <Button
                                        component="span"
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                    >
                                        {language === "en" ? "Upload First Image" : "पहली छवि अपलोड करें"}
                                    </Button>
                                </label>
                            </Paper>
                        ) : (
                            <Grid container spacing={2}>
                                {images.map((image) => (
                                    <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}>
                                        <Card sx={{ position: "relative", overflow: "visible" }}>
                                            {/* Image */}
                                            <Box
                                                sx={{
                                                    position: "relative",
                                                    paddingTop: "100%", // 1:1 aspect ratio
                                                    cursor: "pointer",
                                                }}
                                            >
                                                <img
                                                    src={image.mobile_image || image.url}
                                                    alt={currentTagName}
                                                    style={{
                                                        position: "absolute",
                                                        top: 0,
                                                        left: 0,
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                        borderRadius: "4px 4px 0 0",
                                                    }}
                                                    onClick={() => window.open(image.mobile_image, "_blank")}
                                                />

                                                {/* Action buttons overlay */}
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        top: 8,
                                                        right: 8,
                                                        display: "flex",
                                                        gap: 1,
                                                    }}
                                                >
                                                    {/* Primary button */}
                                                    <Tooltip
                                                        title={
                                                            image.is_primary
                                                                ? (language === "en" ? "Primary image" : "प्राथमिक छवि")
                                                                : (language === "en" ? "Set as primary" : "प्राथमिक के रूप में सेट करें")
                                                        }
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            sx={{
                                                                bgcolor: "rgba(255,255,255,0.9)",
                                                                '&:hover': { bgcolor: "white" },
                                                            }}
                                                            onClick={() => !image.is_primary && handleSetPrimary(image.id)}
                                                            disabled={image.is_primary}
                                                        >
                                                            {image.is_primary ? (
                                                                <StarIcon sx={{ color: "#ff9800" }} />
                                                            ) : (
                                                                <StarBorderIcon />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>

                                                    {/* Edit button */}
                                                    <Tooltip title={language === "en" ? "Edit" : "संपादित करें"}>
                                                        <IconButton
                                                            size="small"
                                                            sx={{
                                                                bgcolor: "rgba(255,255,255,0.9)",
                                                                '&:hover': { bgcolor: "white" },
                                                            }}
                                                            onClick={() => {
                                                                setSelectedImage(image);
                                                                setEditDialogOpen(true);
                                                            }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={language === "en" ? "Move" : "Move"}>
                                                        <IconButton
                                                            size="small"
                                                            sx={{
                                                                bgcolor: "rgba(255,255,255,0.9)",
                                                                '&:hover': { bgcolor: "white" },
                                                            }}
                                                            onClick={() => {
                                                                setSelectedImage(image);
                                                                setSelectedTag(image?.tag_name);
                                                                setTagDialogOpen(true);
                                                            }}
                                                        >
                                                            <DriveFileMoveIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {/* Delete button */}
                                                    <Tooltip title={language === "en" ? "Delete" : "हटाएं"}>
                                                        <IconButton
                                                            size="small"
                                                            sx={{
                                                                bgcolor: "rgba(255,255,255,0.9)",
                                                                '&:hover': { bgcolor: "white" },
                                                            }}
                                                            onClick={() => {
                                                                setImageToDelete(image);
                                                                setDeleteDialogOpen(true);
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>

                                                {/* Status badge */}
                                                {image.status && (
                                                    <Chip
                                                        label={image.status === "approved"
                                                            ? (language === "en" ? "Approved" : "अनुमोदित")
                                                            : (language === "en" ? "Review" : "समीक्षा")
                                                        }
                                                        size="small"
                                                        color={image.status === "approved" ? "success" : "warning"}
                                                        sx={{
                                                            position: "absolute",
                                                            bottom: 8,
                                                            left: 8,
                                                            fontSize: "0.7rem",
                                                        }}
                                                    />
                                                )}
                                            </Box>

                                            {/* Primary indicator */}
                                            {image.is_primary && (
                                                <Box sx={{ p: 1, textAlign: "center" }}>
                                                    <Chip
                                                        icon={<StarIcon />}
                                                        label={language === "en" ? "Primary" : "प्राथमिक"}
                                                        size="small"
                                                        color="primary"
                                                    />
                                                </Box>
                                            )}
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}

                        {/* Floating action button for quick upload */}
                        <Fab
                            color="primary"
                            sx={{
                                position: "fixed",
                                bottom: 20,
                                right: 20,
                            }}
                            onClick={() => document.getElementById("image-upload")?.click()}
                            disabled={uploading}
                        >
                            {uploading ? <CircularProgress size={24} color="inherit" /> : <AddIcon />}
                        </Fab>
                    </Container>

                    {/* Edit Image Dialog */}
                    <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                        <DialogTitle>
                            {language === "en" ? "Edit Image" : "छवि संपादित करें"}
                        </DialogTitle>
                        <DialogContent>
                            {selectedImage && (
                                <Box sx={{ textAlign: "center", my: 2 }}>
                                    <img
                                        src={selectedImage.mobile_image}
                                        alt="Current"
                                        style={{
                                            maxWidth: "100%",
                                            maxHeight: 300,
                                            objectFit: "contain",
                                            borderRadius: 8,
                                            border: "1px solid #ddd",
                                        }}
                                    />
                                </Box>
                            )}
                            <TextField
                                type="file"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ accept: "image/*" }}
                                onChange={(e) => setEditImageFile(e.target.files[0])}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setEditDialogOpen(false)}>
                                {language === "en" ? "Cancel" : "रद्द करें"}
                            </Button>
                            <Button
                                onClick={handleUpdateImage}
                                variant="contained"
                                disabled={!editImageFile}
                                startIcon={<CheckIcon />}
                            >
                                {language === "en" ? "Update" : "अपडेट करें"}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Delete Confirmation Dialog */}
                    <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                        <DialogTitle>
                            {language === "en" ? "Confirm Delete" : "हटाने की पुष्टि करें"}
                        </DialogTitle>
                        <DialogContent>
                            <Typography>
                                {language === "en"
                                    ? "Are you sure you want to delete this image?"
                                    : "क्या आप वाकई इस छवि को हटाना चाहते हैं?"}
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDeleteDialogOpen(false)}>
                                {language === "en" ? "Cancel" : "रद्द करें"}
                            </Button>
                            <Button
                                onClick={handleDeleteImage}
                                variant="contained"
                                color="error"
                                startIcon={<DeleteIcon />}
                            >
                                {language === "en" ? "Delete" : "हटाएं"}
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <Dialog
                        open={tagDialogOpen}
                        onClose={() => setTagDialogOpen(false)}
                        fullWidth
                        maxWidth="sm"
                    >
                        <DialogTitle>
                            {language === "en" ? "Select Image Category" : "इमेज कैटेगरी चुनें"}
                        </DialogTitle>

                        <DialogContent>
                            <RadioGroup
                                value={selectedTag}
                                onChange={(e) => setSelectedTag(e.target.value)}
                            >
                                {tags.map((tag) => (
                                    <FormControlLabel
                                        key={tag.id}
                                        value={tag.id}
                                        control={<Radio />}
                                        label={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                {tag.icon}
                                                <Typography>{tag.name}</Typography>
                                            </Box>
                                        }
                                    />
                                ))}
                            </RadioGroup>
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={() => setEditDialogOpen(false)}>
                                {language === "en" ? "Cancel" : "रद्द करें"}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleUpdateTag}
                            >
                                {language === "en" ? "Save" : "सेव करें"}
                            </Button>
                        </DialogActions>
                    </Dialog>

                </div>
            </div>
        </div>
    );
};

export default TagImageManagement;